import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, MapPin, ShieldCheck, Award, Truck } from 'lucide-react';

const values = [
  { id: 'V-01', icon: ShieldCheck, title: 'Safety First', desc: 'Every component is checked, serviced and certified before dispatch — no shortcuts.' },
  { id: 'V-02', icon: Award, title: 'Quality Material', desc: 'Heavy-duty, load-tested grades only. We do not compromise on steel.' },
  { id: 'V-03', icon: Truck, title: 'Reliability', desc: 'On-time delivery, on-time pickup. Your project schedule is our schedule.' },
];

const About = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="about-page">
      {/* Hero (teal) */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-[#008080] via-[#0a7878] to-[#005a5a] text-white">
        <div className="absolute inset-0 bp-grid-on-dark opacity-50"></div>
        <div className="spotlight-gold bottom-0 right-0"></div>
        <span className="serial-ghost serial-ghost-light absolute right-4 top-12 text-[12rem] hidden md:block">ABOUT</span>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="ark-overline ark-overline-light">// who we are</span>
          <h1 className="font-display font-black uppercase tracking-tighter text-white mt-5 text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.88]">
            About<br />
            <span className="text-[#DAA520]">ARK Scaffolding.</span>
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="relative py-20 md:py-24 bg-white">
        <div className="absolute inset-0 bp-grid-fine opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="ark-overline">// 01 · Our work</span>
            <h2 className="font-display font-bold text-[#008080] text-3xl md:text-5xl mt-4 leading-[0.95]">Dependable scaffolding, on hire.</h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-gray-700 text-base md:text-lg leading-relaxed">
            <p>
              ARK Scaffolding provides dependable scaffolding material on hire for residential, commercial and industrial construction projects across Pune, Pimpri Chinchwad, Mumbai and Maharashtra.
            </p>
            <p>
              Our focus is on supplying strong, well-maintained, site-ready scaffolding material that supports safe and efficient work at height — Cuplock systems, H Frames, props, jacks, couplers and the full accessory line.
            </p>
            <p>
              We work directly with site engineers, contractors and project managers. Quick response, fair rentals, no surprises.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-20 md:py-24 bg-gray-50 border-t border-gray-200">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="ark-overline">// 02 · How we operate</span>
          <h2 className="font-display font-bold text-[#008080] text-4xl md:text-5xl mt-4 mb-12 leading-[0.95]">Three things, every dispatch.</h2>
          <div className="grid sm:grid-cols-3 gap-px bg-gray-200">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.id} className="ark-card-beam bg-white p-8 group min-h-[260px] relative">
                  <span className="serial-ghost absolute -top-2 right-4 text-[6rem]">{v.id.split('-')[1]}</span>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-11 h-11 border border-[#008080]/30 bg-[#008080]/5 flex items-center justify-center rounded">
                        <Icon className="text-[#008080]" size={20} />
                      </div>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-gray-400">{v.id}</span>
                    </div>
                    <h3 className="font-display font-bold text-[#008080] text-xl md:text-2xl leading-tight">{v.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mt-3">{v.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Address */}
      <section className="relative py-20 md:py-24 bg-white border-t border-gray-200">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="ark-overline">// 03 · Visit us</span>
            <h2 className="font-display font-bold text-[#008080] text-3xl md:text-5xl mt-4 leading-[0.95]">Yard location.</h2>
            <p className="text-gray-600 mt-4">Drop in to inspect material before dispatch — we encourage it.</p>
          </div>
          <div className="lg:col-span-7 ark-card-beam bg-white p-8 md:p-10 border border-gray-200 rounded">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 border border-[#008080]/30 bg-[#008080]/5 flex items-center justify-center flex-shrink-0 rounded">
                <MapPin className="text-[#008080]" size={20} />
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">// Headquarters · MH-PUNE</div>
                <p className="text-gray-900 text-lg md:text-xl leading-relaxed">
                  Survey Number: 148/5,<br />
                  Opp. Sandeep Kate Bunglow,<br />
                  Pimple Saudagar,<br />
                  Pune — 411027, Maharashtra
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA (teal) */}
      <section className="relative py-20 md:py-24 overflow-hidden bg-gradient-to-br from-[#008080] to-[#005a5a] text-white">
        <div className="absolute inset-0 bp-grid-on-dark opacity-40"></div>
        <div className="spotlight-gold left-1/2 -translate-x-1/2 -top-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-black uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.9]">
            Need scaffolding<br /><span className="text-[#DAA520]">for your project?</span>
          </h2>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+919766601155" className="ark-btn-primary"><Phone size={16} /> Call +91 9766601155</a>
            <Link to="/contact?source=hero_cta" className="ark-btn-secondary ark-btn-secondary-dark"><MessageCircle size={16} /> Get a Quote</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
